import { useEffect, useState } from "react";
import { Swords, Trophy, Loader2, CalendarClock, RefreshCw, ListChecks, ChevronDown, ChevronUp, Calculator, Rocket, Network, History } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageShell } from "@/components/Layout";
import { HailMaryPanel } from "@/components/HailMaryPanel";
import { PlayoffPreview, RealPlayoffs, RealProjection, RealWiringTest } from "@/components/ShowdownPlayoffs";
import { previewToolsEnabled } from "@/lib/previewTools";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

function MatchHistory({ history }) {
  const [open, setOpen] = useState(null);
  if (!history) return null;
  const finalized = history.finalized || [];
  const pending = history.pending || [];
  if (!finalized.length && !pending.length) return null;
  const color = (r) => (r === "WIN" ? "text-emerald-400" : r === "LOSS" ? "text-red-400" : "text-amber-400");
  return (
    <div className="mb-8" data-testid="match-history">
      <div className="flex items-center gap-2 mb-3">
        <History className="h-4 w-4 neon-text" />
        <h2 className="font-display text-sm font-bold uppercase tracking-wider">Previous Showdown Matches</h2>
      </div>
      {pending.map((p) => (
        <div key={`p-${p.week}`} data-testid={`history-pending-${p.week}`} className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-4 mb-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-wide text-amber-300/80">{p.week_label}</div>
              <div className="text-sm font-semibold mt-0.5 truncate">vs {p.opponent}</div>
            </div>
            <span className="shrink-0 text-[10px] font-bold uppercase text-amber-300 bg-amber-500/15 px-2 py-1 rounded">Pending Finalization</span>
          </div>
        </div>
      ))}
      {finalized.map((mt) => {
        const isOpen = open === mt.week;
        return (
          <div key={mt.week} data-testid={`history-match-${mt.week}`} className="rounded-xl border border-[#1F1F22] bg-[#0A0A0A] p-4 mb-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">{mt.week_label}</div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <span className={`font-display font-black text-sm ${color(mt.result)}`} data-testid={`history-result-${mt.week}`}>{mt.result}</span>
                  <span className="text-sm font-semibold">{mt.me.display_name} {mt.me.total} – {mt.opponent.total} {mt.opponent.display_name}</span>
                </div>
              </div>
              <button data-testid={`history-breakdown-toggle-${mt.week}`} onClick={() => setOpen(isOpen ? null : mt.week)}
                className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-[#4C8DFF] hover:text-[#7ab0ff]">
                {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} View Breakdown
              </button>
            </div>
            {isOpen && (
              <div className="mt-3 pt-3 border-t border-[#1F1F22] grid grid-cols-2 gap-3 text-xs" data-testid={`history-breakdown-${mt.week}`}>
                {[mt.me, mt.opponent].map((s, i) => {
                  const winnerOnly = mt.scoring === "winner_only";
                  return (
                    <div key={i} className={i === 0 ? "" : "text-right"}>
                      <div className="font-bold text-sm text-white">{s.display_name}</div>
                      <div className="text-zinc-400 mt-1 space-y-0.5">
                        <div>Winner{winnerOnly ? " Points" : ""}: <b className="text-white">{s.winner}</b></div>
                        {!winnerOnly && <div>Passing: <b className="text-white">{s.passing}</b></div>}
                        {!winnerOnly && <div>Rushing: <b className="text-white">{s.rushing}</b></div>}
                        {(!winnerOnly || (typeof s.hail_mary === "number" && s.hail_mary > 0)) && (
                          <div>Hail Mary: <b className="text-purple-300">{s.hail_mary}</b></div>
                        )}
                        <div className="font-display font-bold neon-text mt-1">TOTAL: {s.total}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ScoreSide({ s, highlight, scoring }) {
  if (!s) return <div className="flex-1" />;
  const winnerOnly = scoring === "winner_only";
  return (
    <div className={`flex-1 text-center rounded-xl px-2 py-1 ${highlight ? "bg-emerald-500/10 ring-1 ring-emerald-500/40" : ""}`}>
      <div className="font-display text-lg font-black">{s.display_name}</div>
      <div className="text-xs text-zinc-400 mt-2 space-y-0.5">
        <div>Winner{winnerOnly ? " Points" : ""}: <span className="font-bold text-white">{s.winner}</span></div>
        {!winnerOnly && <div>Passing: <span className="font-bold text-white">{s.passing}</span></div>}
        {!winnerOnly && <div>Rushing: <span className="font-bold text-white">{s.rushing}</span></div>}
        {(!winnerOnly || (typeof s.hail_mary === "number" && s.hail_mary > 0)) && (
          <div>Hail Mary: {typeof s.hail_mary === "number"
            ? <span className="font-semibold text-purple-300">{s.hail_mary}</span>
            : <span className="font-semibold text-purple-300">Not Active</span>}</div>
        )}
      </div>
      <div className="mt-2 font-display text-2xl font-black neon-text">{s.total} <span className="text-xs text-zinc-500">pts</span></div>
    </div>
  );
}

function ShowdownScoreboard({ sb, title }) {
  if (!sb) return null;
  const finalized = sb.finalized;
  const winnerOnly = sb.scoring === "winner_only";
  const meWon = finalized && !sb.is_tie && sb.winner_id && sb.winner_id === sb.me?.user_id;
  const oppWon = finalized && !sb.is_tie && sb.opponent && sb.winner_id === sb.opponent.user_id;
  const winnerName = finalized ? (sb.is_tie ? null : (meWon ? sb.me.display_name : (oppWon ? sb.opponent.display_name : null))) : null;
  return (
    <div className="rounded-2xl border border-[#D50A0A]/40 bg-[#D50A0A]/5 p-6 mb-8" data-testid="showdown-scoreboard">
      <div className="flex items-center gap-2 mb-4">
        <Swords className="h-4 w-4 text-[#ff5b5b]" />
        <span className="overline text-xs">{title || "This Week's Showdown Score"}</span>
        <span className="ml-auto text-xs font-bold text-zinc-400 uppercase">{sb.week_label}</span>
      </div>
      <div className="flex items-stretch gap-3">
        <ScoreSide s={sb.me} highlight={meWon} scoring={sb.scoring} />
        <div className="flex items-center font-display text-sm font-black text-zinc-600">VS</div>
        <ScoreSide s={sb.opponent} highlight={oppWon} scoring={sb.scoring} />
      </div>
      <div className="mt-3 text-center">
        {finalized ? (
          <span data-testid="scoreboard-status" className="text-xs font-bold uppercase text-emerald-400">
            Final{sb.is_tie ? " · Tie" : winnerName ? ` · ${winnerName} wins` : ""}
          </span>
        ) : (
          <span data-testid="scoreboard-status" className="text-xs font-bold uppercase text-[#ff7a7a]">In Progress</span>
        )}
      </div>
      {!winnerOnly && typeof sb.games_final === "number" && sb.games_final > 0 && (
        <p data-testid="scoreboard-grading" className="text-[11px] text-zinc-500 mt-2 text-center">
          Passing/Rushing graded for {sb.games_graded}/{sb.games_final} completed games
          {sb.stats_pending > 0 ? ` · ${sb.stats_pending} still pending` : " · all graded"}
        </p>
      )}
      {sb.pending && <p className="text-[11px] text-zinc-500 mt-1 text-center">Opponent's per-game picks stay hidden until each game kicks off.</p>}
    </div>
  );
}

function Badge({ text, tone }) {
  const tones = {
    current: "bg-[#D50A0A]/20 text-[#ff7a7a]",
    beta: "bg-amber-500/15 text-amber-300",
    upcoming: "bg-zinc-700/40 text-zinc-300",
    final: "bg-emerald-500/15 text-emerald-400",
    bye: "bg-amber-500/15 text-amber-300",
  };
  return <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${tones[tone] || tones.upcoming}`}>{text}</span>;
}

function weekBadges(wk, mm) {
  const out = [];
  if (wk.is_current) out.push(["current", "Current"]);
  if (wk.is_beta) out.push(["beta", "Beta"]);
  if (!wk.is_current && !wk.is_beta) out.push(["upcoming", "Upcoming"]);
  if (mm?.status === "final") out.push(["final", "Final"]);
  if (mm?.is_bye) out.push(["bye", "Bye"]);
  return out;
}

// A single week row for the PLAYER's personal schedule
function MyWeekRow({ wk }) {
  const mm = wk.my_matchup;
  return (
    <div data-testid={`my-week-${wk.index}`}
      className={`rounded-xl border px-4 py-3 flex items-center gap-3 ${wk.is_current ? "border-[#D50A0A]/40 bg-[#D50A0A]/5" : "border-[#1F1F22] bg-[#0A0A0A]"}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-display text-sm font-bold uppercase tracking-wide">{wk.label}</span>
          {weekBadges(wk, mm).map(([tone, txt]) => <Badge key={tone} text={txt} tone={tone} />)}
        </div>
        <div className="text-sm mt-1">
          {!mm ? <span className="text-zinc-500">Not scheduled</span>
            : mm.is_bye ? <span className="text-amber-400 font-semibold">BYE week</span>
            : <span><span className="text-zinc-400">vs</span> <span className="font-bold">{mm.opponent}</span>
                {mm.status === "final" && <span className="text-zinc-400"> · {mm.points_a}–{mm.points_b}</span>}</span>}
        </div>
      </div>
    </div>
  );
}

// A single week block for the COMMISSIONER (all league pairings)
function LeagueWeekBlock({ wk }) {
  return (
    <div data-testid={`league-week-${wk.index}`} className="border-b border-[#141416] last:border-0">
      <div className="px-5 pt-4 pb-2 flex items-center gap-2 flex-wrap">
        <span className="font-display text-sm font-bold uppercase tracking-wide">{wk.label}</span>
        {wk.is_current && <Badge text="Current" tone="current" />}
        {wk.is_beta && <Badge text="Beta" tone="beta" />}
        {!wk.is_current && !wk.is_beta && <Badge text="Upcoming" tone="upcoming" />}
      </div>
      <div className="px-5 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {wk.matchups.length === 0 && <span className="text-xs text-zinc-600">No matchups scheduled</span>}
        {wk.matchups.map((m) => (
          <div key={m.id} className="flex items-center gap-2 text-sm rounded-lg bg-[#0F0F10] border border-[#161618] px-3 py-2">
            <Badge text={m.status} tone={m.status === "final" ? "final" : m.is_bye ? "bye" : "upcoming"} />
            {m.is_bye ? (
              <span><span className="font-semibold">{m.player_a?.display_name}</span> <span className="text-amber-400 text-xs font-bold">· BYE</span></span>
            ) : (
              <span className="truncate">
                <span className="font-semibold">{m.player_a?.display_name}</span>
                <span className="text-zinc-500"> vs </span>
                <span className="font-semibold">{m.player_b?.display_name}</span>
                {m.status === "final" && <span className="text-zinc-400"> · {m.points_a}–{m.points_b}</span>}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Commissioner control to grant bonus Hail Mary cards (real regular-season grants)
function GrantHailMary({ players }) {
  const [scope, setScope] = useState("all");
  const [userId, setUserId] = useState("");
  const [amount, setAmount] = useState(3);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/showdown/hailmary").then(({ data }) => setActive(!!data.active)).catch(() => setActive(false));
  }, []);

  const submit = async () => {
    const n = parseInt(amount, 10);
    if (!n || n < 1) { toast.error("Enter the number of cards to grant"); return; }
    if (scope === "one" && !userId) { toast.error("Select a player"); return; }
    const who = scope === "all" ? "all players" : (players.find((p) => p.user_id === userId)?.display_name || "this player");
    if (!window.confirm(`Grant ${n} additional Hail Mary card${n > 1 ? "s" : ""} to ${who} for the 2026 season?`)) return;
    setBusy(true);
    try {
      const grant_id = (window.crypto?.randomUUID?.() || `g-${Date.now()}`);
      const { data } = await api.post("/showdown/hailmary/grant",
        { scope, user_id: scope === "one" ? userId : null, amount: n, grant_id });
      if (data.duplicate) toast.info("That grant was already applied.");
      else toast.success(`Granted +${n} Hail Mary card${n > 1 ? "s" : ""} to ${who}.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // Regular-season only: mirror the backend gate in the UI.
  if (active === false) {
    return (
      <div className="rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] p-5 mb-8" data-testid="grant-hailmary-disabled">
        <div className="flex items-center gap-2">
          <Rocket className="h-4 w-4 text-zinc-500" />
          <h2 className="font-display text-sm font-bold uppercase tracking-wider text-zinc-400">Grant Hail Mary Cards</h2>
          <span className="ml-auto text-xs text-zinc-500">Available during the Regular Season.</span>
        </div>
      </div>
    );
  }
  if (active === null) return null;

  return (
    <div className="rounded-2xl border border-purple-500/30 bg-purple-500/5 p-5 mb-8" data-testid="grant-hailmary">
      <div className="flex items-center gap-2 mb-3">
        <Rocket className="h-4 w-4 text-purple-400" />
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-purple-200">Grant Hail Mary Cards</h2>
        <span className="ml-auto text-xs text-zinc-500">Adds to seasonal allocation · regular season only</span>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Recipient</label>
          <select data-testid="grant-scope-select" value={scope}
            onChange={(e) => { setScope(e.target.value); setUserId(""); }}
            className="rounded-lg bg-[#121212] border border-[#1F1F22] px-3 py-2 text-sm">
            <option value="all">All players</option>
            <option value="one">One player</option>
          </select>
        </div>
        {scope === "one" && (
          <div>
            <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Player</label>
            <select data-testid="grant-player-select" value={userId} onChange={(e) => setUserId(e.target.value)}
              className="rounded-lg bg-[#121212] border border-[#1F1F22] px-3 py-2 text-sm min-w-[160px]">
              <option value="">Select a player…</option>
              {players.map((p) => <option key={p.user_id} value={p.user_id}>{p.display_name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Cards</label>
          <input data-testid="grant-amount-input" type="number" min="1" max="20" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-20 rounded-lg bg-[#121212] border border-[#1F1F22] px-3 py-2 text-sm text-center" />
        </div>
        <button data-testid="grant-submit-btn" onClick={submit} disabled={busy}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-purple-600 text-white font-bold active:scale-95 transition-transform disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} Grant Cards
        </button>
      </div>
    </div>
  );
}

export default function Showdown() {
  const { user } = useAuth();
  const isAdmin = user && (user.role === "admin" || user.role === "owner");
  const previewTools = previewToolsEnabled();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [hmPreview, setHmPreview] = useState(false);
  const [playoffPreview, setPlayoffPreview] = useState(false);
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);
  const [showRegenRegularConfirm, setShowRegenRegularConfirm] = useState(false);
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [showBetaHistory, setShowBetaHistory] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/showdown/season");
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const generateRegular = async (force) => {
    setBusy(true);
    try {
      const { data } = await api.post("/showdown/generate-regular", { force });
      if (data.status === "generated") toast.success(`Regular-season schedule built: ${data.matchups} matchups over ${data.weeks} weeks`);
      else if (data.status === "exists") toast.info("Regular-season schedule already exists.");
      else toast.warning(data.message || "Could not generate schedule");
      setShowRegenRegularConfirm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const generatePreseason = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/showdown/generate", {});
      if (data.status === "generated") toast.success(`Preseason beta schedule built: ${data.matchups} matchups`);
      else if (data.status === "exists") toast.info("Preseason beta schedule already exists — nothing changed.");
      else toast.warning(data.message || "Could not generate preseason schedule");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const regeneratePreseason = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/showdown/regenerate-preseason", {});
      toast.success(`Preseason Week 3 matchups regenerated for ${data.players} players. Existing picks were preserved.`);
      setShowRegenConfirm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const activateRegular = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/showdown/activate-regular", {});
      if (data.status === "already_active") toast.info("Regular Season is already active.");
      else toast.success(`Regular Season activated — ${data.players} players, ${data.matchups} Week 1 matchups.`);
      setShowActivateConfirm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const windowWeeks = data ? data.window.map((i) => data.weeks[i]) : [];

  const updateScores = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/showdown/update-scores");
      if (data.finalized) toast.success(`Showdown finalized · ${data.updated} matchups`);
      else if (data.reason === "boxscore_api_not_configured")
        toast.info(`Winner points updated (${data.updated}). Passing/Rushing pending — Boxscore stats API not connected yet.`);
      else toast.success(`Showdown totals updated · ${data.updated} matchups`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell testid="showdown-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
        <div>
          <span className="overline flex items-center gap-1.5"><Swords className="h-3.5 w-3.5" /> Head-to-Head Mode</span>
          <h1 className="font-display text-4xl font-black tracking-tight mt-2">PickHouse Showdown</h1>
        </div>
        {isAdmin && data && (
          <div className="flex items-center gap-2">
            <button data-testid="update-scores-btn" onClick={updateScores} disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-[#1F1F22] text-sm font-semibold hover:border-emerald-500 hover:text-emerald-400 transition-colors disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />} Update Showdown Scores
            </button>
            {!data.regular_active && (!data.beta_generated ? (
              <button data-testid="generate-preseason-btn" onClick={generatePreseason} disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-amber-500/50 text-sm font-semibold text-amber-300 hover:border-amber-400 hover:text-amber-200 transition-colors disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Swords className="h-4 w-4" />} Generate Preseason Beta Schedule
              </button>
            ) : (
              <button data-testid="regenerate-preseason-btn" onClick={() => setShowRegenConfirm(true)} disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-amber-500/50 text-sm font-semibold text-amber-300 hover:border-amber-400 hover:text-amber-200 transition-colors disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Regenerate Preseason Week 3
              </button>
            ))}
            {data.regular_active ? (
              <span data-testid="regular-active-pill" className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-emerald-500/50 text-sm font-bold text-emerald-400">
                <Trophy className="h-4 w-4" /> Regular Season Active
              </span>
            ) : !data.regular_generated ? (
              <button data-testid="generate-regular-btn" onClick={() => generateRegular(false)} disabled={busy}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#D50A0A] text-white font-bold active:scale-95 transition-transform disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />} Generate Regular-Season Schedule
              </button>
            ) : (
              <>
                <button data-testid="regenerate-regular-btn" onClick={() => setShowRegenRegularConfirm(true)} disabled={busy}
                  className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-[#1F1F22] text-sm font-semibold hover:border-[#D50A0A] hover:text-[#D50A0A] transition-colors disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Regenerate Regular-Season Schedule
                </button>
                <button data-testid="activate-regular-btn" onClick={() => setShowActivateConfirm(true)} disabled={busy || !(data.regular_preflight?.ok)}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-600 text-white font-bold active:scale-95 transition-transform disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trophy className="h-4 w-4" />} Activate Regular Season
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <Dialog open={showActivateConfirm} onOpenChange={setShowActivateConfirm}>
        <DialogContent className="bg-[#0A0A0A] border-[#1F1F22] text-white" data-testid="activate-regular-dialog">
          <DialogHeader><DialogTitle className="font-display">Activate Regular Season?</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 text-sm text-zinc-400">
            <p>PickHouse is ready to begin the Regular Season.</p>
            <div className="flex gap-4 text-white font-semibold">
              <span data-testid="activate-players">Players: {data?.regular_preflight?.players ?? "—"}</span>
              <span data-testid="activate-matchups">Week 1 Matchups: {data?.regular_preflight?.matchups ?? "—"}</span>
              <span data-testid="activate-byes">Byes: {data?.regular_preflight?.byes ?? "—"}</span>
            </div>
            <ul className="list-disc pl-5 space-y-1 text-xs">
              <li>Regular Season Showdown becomes the official competition</li>
              <li>Everyone begins 0-0-0 with 0 Regular Season Showdown points</li>
              <li>The Regular Season schedule becomes locked</li>
              <li>Preseason Beta results remain separate and will not carry over</li>
              <li>Hail Mary becomes available under the existing Regular Season rules</li>
            </ul>
            {!(data?.regular_preflight?.ok) && (
              <p className="text-[#ff7a7a] text-xs">Preflight failed: {(data?.regular_preflight?.errors || []).join("; ")}</p>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <button data-testid="confirm-activate-btn" onClick={activateRegular} disabled={busy || !(data?.regular_preflight?.ok)}
              className="w-full py-3 rounded-full bg-emerald-600 text-white font-bold active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Activate Regular Season
            </button>
            <button data-testid="cancel-activate-btn" onClick={() => setShowActivateConfirm(false)}
              className="w-full py-3 rounded-full border border-[#1F1F22] text-sm font-semibold text-zinc-300 hover:border-[#D50A0A] hover:text-white transition-colors">
              Cancel
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div data-testid="showdown-beta-badge" className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold mb-8 ${data?.regular_active ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`}>
        {data?.regular_active ? "REGULAR SEASON ACTIVE · OFFICIAL COMPETITION" : "PRESEASON BETA · REGULAR SEASON SCHEDULE STORED (NOT YET ACTIVE)"}
      </div>

      <Dialog open={showRegenConfirm} onOpenChange={setShowRegenConfirm}>
        <DialogContent className="bg-[#0A0A0A] border-[#1F1F22] text-white" data-testid="regen-preseason-dialog">
          <DialogHeader><DialogTitle className="font-display">Regenerate Preseason Week 3?</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2 text-sm text-zinc-400">
            <p>This will rebuild the head-to-head matchups using the current player roster.</p>
            <p><span className="text-emerald-400 font-semibold">Any Winner, Passing or Rushing picks already made will NOT be changed.</span></p>
            <p>Existing preseason head-to-head pairings will be replaced.</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <button data-testid="confirm-regen-preseason-btn" onClick={regeneratePreseason} disabled={busy}
              className="w-full py-3 rounded-full bg-[#D50A0A] text-white font-bold active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Regenerate Matchups
            </button>
            <button data-testid="cancel-regen-preseason-btn" onClick={() => setShowRegenConfirm(false)}
              className="w-full py-3 rounded-full border border-[#1F1F22] text-sm font-semibold text-zinc-300 hover:border-[#D50A0A] hover:text-white transition-colors">
              Cancel
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showRegenRegularConfirm} onOpenChange={setShowRegenRegularConfirm}>
        <DialogContent className="bg-[#0A0A0A] border-[#1F1F22] text-white" data-testid="regen-regular-dialog">
          <DialogHeader><DialogTitle className="font-display">Regenerate Regular-Season Schedule?</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2 text-sm text-zinc-400">
            <p>This will rebuild Weeks 1–18 using the current active player roster.</p>
            <p><span className="text-emerald-400 font-semibold">The Regular Season has not started, so no standings or regular-season results will be affected.</span></p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <button data-testid="confirm-regen-regular-btn" onClick={() => generateRegular(true)} disabled={busy}
              className="w-full py-3 rounded-full bg-[#D50A0A] text-white font-bold active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Regenerate Schedule
            </button>
            <button data-testid="cancel-regen-regular-btn" onClick={() => setShowRegenRegularConfirm(false)}
              className="w-full py-3 rounded-full border border-[#1F1F22] text-sm font-semibold text-zinc-300 hover:border-[#D50A0A] hover:text-white transition-colors">
              Cancel
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* COMMISSIONER — Hail Mary preview/test (mock only, no DB writes) · PREVIEW-ONLY */}
      {isAdmin && previewTools && (
        <div className="mb-8" data-testid="hm-preview-section">
          <button data-testid="hm-preview-toggle" onClick={() => setHmPreview((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-purple-500/40 bg-purple-500/10 text-sm font-semibold text-purple-200 hover:border-purple-400 hover:text-purple-100 transition-colors">
            <Rocket className="h-4 w-4" /> {hmPreview ? "Hide Hail Mary Preview" : "Preview Hail Mary (test)"}
          </button>
          {hmPreview && (
            <>
              <p className="text-xs text-zinc-500 mt-2 max-w-xl">
                Test-only preview of the player Hail Mary flow. Uses mock QBs and writes NOTHING to the database — no real cards used, no points awarded, regular season stays inactive.
              </p>
              <HailMaryPanel preview isAdmin />
            </>
          )}
        </div>
      )}

      {/* COMMISSIONER — Playoff bracket preview (mock only, no DB writes) · PREVIEW-ONLY */}
      {isAdmin && previewTools && (
        <div className="mb-8" data-testid="playoff-preview-section">
          <button data-testid="playoff-preview-toggle" onClick={() => setPlayoffPreview((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#4C8DFF]/40 bg-[#4C8DFF]/10 text-sm font-semibold text-[#9cc0ff] hover:border-[#4C8DFF] hover:text-white transition-colors">
            <Network className="h-4 w-4" /> {playoffPreview ? "Hide Playoff Preview" : "Preview Playoffs (test)"}
          </button>
          {playoffPreview && <PlayoffPreview />}
          <RealWiringTest />
        </div>
      )}

      {loading || !data ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 neon-text animate-spin" /></div>
      ) : (
        <>
          {/* REAL Week 14–18 projection (hidden otherwise) + REAL persisted playoffs */}
          <RealProjection />
          <RealPlayoffs isAdmin={isAdmin} />

          {/* COMMISSIONER — Grant bonus Hail Mary cards */}
          {isAdmin && (
            <GrantHailMary players={data.beta_standings || []} />
          )}

          {/* COMMISSIONER — Scoring inspection (near top) */}
          {isAdmin && data.beta_matchup_scores && data.beta_matchup_scores.length > 0 && (
            <div className="rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] overflow-hidden mb-8" data-testid="beta-scoring">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-[#141416]">
                <Calculator className="h-4 w-4 neon-text" />
                <h2 className="font-display text-sm font-bold uppercase tracking-wider">Preseason Week 3 — Showdown Scoring</h2>
                <span className="ml-auto text-xs text-zinc-500">Winner + Passing + Rushing (1 pt each)</span>
              </div>
              <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
                {data.beta_matchup_scores.filter((m) => !m.is_bye).map((m, idx) => (
                  <div key={idx} className="rounded-xl border border-[#161618] bg-[#0F0F10] p-4">
                    <div className="flex justify-end mb-2">
                      <Badge text={m.status.replace(/_/g, " ")} tone={m.status === "final" ? "final" : m.status === "in_progress" ? "current" : "upcoming"} />
                    </div>
                    <div className="grid grid-cols-[1fr_auto_1fr] gap-2 text-xs items-center">
                      <div>
                        <div className="font-bold text-sm text-white">{m.a.display_name}</div>
                        <div className="text-zinc-500 mt-1">Winner: {m.a.winner} · Passing: {m.a.passing} · Rushing: {m.a.rushing}</div>
                        <div className="text-purple-300/80 mt-0.5">Hail Mary: Not Active</div>
                        <div className="font-display font-bold neon-text mt-1">{m.a.total} pts</div>
                      </div>
                      <div className="font-display text-xs font-black text-zinc-600">VS</div>
                      <div className="text-right">
                        <div className="font-bold text-sm text-white">{m.b.display_name}</div>
                        <div className="text-zinc-500 mt-1">Winner: {m.b.winner} · Passing: {m.b.passing} · Rushing: {m.b.rushing}</div>
                        <div className="text-purple-300/80 mt-0.5">Hail Mary: Not Active</div>
                        <div className="font-display font-bold neon-text mt-1">{m.b.total} pts</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* COMMISSIONER — Regular Season scoring inspection */}
          {isAdmin && data.regular_active && data.regular_matchup_scores && data.regular_matchup_scores.length > 0 && (
            <div className="rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] overflow-hidden mb-8" data-testid="regular-scoring">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-[#141416]">
                <Calculator className="h-4 w-4 neon-text" />
                <h2 className="font-display text-sm font-bold uppercase tracking-wider">{data.regular_scoreboard?.week_label || "Regular Season"} — Showdown Scoring</h2>
                <span className="ml-auto text-xs text-zinc-500">
                  {data.regular_scoreboard?.scoring === "winner_only" ? "Winner picks only (escalating by week)" : "Winner + Passing + Rushing (1 pt each)"}
                </span>
              </div>
              <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
                {data.regular_matchup_scores.filter((m) => !m.is_bye).map((m, idx) => {
                  const winnerOnly = m.scoring === "winner_only";
                  const line = (s) => winnerOnly
                    ? `Winner Points: ${s.winner}`
                    : `Winner: ${s.winner} · Passing: ${s.passing} · Rushing: ${s.rushing}`;
                  return (
                    <div key={idx} className="rounded-xl border border-[#161618] bg-[#0F0F10] p-4">
                      <div className="flex justify-end mb-2">
                        <Badge text={m.status.replace(/_/g, " ")} tone={m.status === "final" ? "final" : m.status === "in_progress" ? "current" : "upcoming"} />
                      </div>
                      <div className="grid grid-cols-[1fr_auto_1fr] gap-2 text-xs items-center">
                        <div>
                          <div className="font-bold text-sm text-white">{m.a.display_name}</div>
                          <div className="text-zinc-500 mt-1">{line(m.a)}</div>
                          {(!winnerOnly || m.a.hail_mary > 0) && <div className="text-purple-300/80 mt-0.5">Hail Mary: {winnerOnly ? m.a.hail_mary : "Not Active"}</div>}
                          <div className="font-display font-bold neon-text mt-1">{m.a.total} pts</div>
                        </div>
                        <div className="font-display text-xs font-black text-zinc-600">VS</div>
                        <div className="text-right">
                          <div className="font-bold text-sm text-white">{m.b.display_name}</div>
                          <div className="text-zinc-500 mt-1">{line(m.b)}</div>
                          {(!winnerOnly || m.b.hail_mary > 0) && <div className="text-purple-300/80 mt-0.5">Hail Mary: {winnerOnly ? m.b.hail_mary : "Not Active"}</div>}
                          <div className="font-display font-bold neon-text mt-1">{m.b.total} pts</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* PLAYER — Current-week Showdown score (regular hides finalized beta card) */}
          {!data.regular_active && data.beta_scoreboard && <ShowdownScoreboard sb={data.beta_scoreboard} />}
          {data.regular_active && data.regular_scoreboard && !data.regular_scoreboard.is_bye && (
            <ShowdownScoreboard sb={data.regular_scoreboard} title="This Week's Showdown" />
          )}
          {data.regular_active && data.regular_scoreboard && data.regular_scoreboard.is_bye && (
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 mb-8" data-testid="showdown-bye-card">
              <span className="overline text-xs">This Week's Showdown</span>
              <p className="text-sm text-zinc-300 mt-2">You're on a <span className="font-bold text-amber-300">BYE</span> this week ({data.regular_scoreboard.week_label}).</p>
            </div>
          )}

          {/* PLAYER — My Showdown Schedule */}
          {data.is_participant && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <ListChecks className="h-4 w-4 neon-text" />
                <h2 className="font-display text-sm font-bold uppercase tracking-wider">My Showdown Schedule</h2>
                <span className="ml-auto text-xs text-zinc-500">Current + next 3 weeks</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="my-schedule-window">
                {windowWeeks.map((wk) => <MyWeekRow key={wk.index} wk={wk} />)}
              </div>
              <button data-testid="toggle-full-schedule" onClick={() => setShowFull((v) => !v)}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#4C8DFF] hover:text-[#7ab0ff]">
                {showFull ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                {showFull ? "Hide full schedule" : "View full schedule"}
              </button>
              {showFull && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="my-full-schedule">
                  {data.weeks.map((wk) => <MyWeekRow key={wk.index} wk={wk} />)}
                </div>
              )}
            </div>
          )}

          {/* PLAYER — Previous Showdown Matches (finalized history + pending backlog) */}
          {data.is_participant && <MatchHistory history={data.match_history} />}

          {/* COMMISSIONER — Full league schedule */}
          {isAdmin && (
            <div className="rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] overflow-hidden mb-8" data-testid="league-schedule">
              <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-b border-[#141416]">
                <ListChecks className="h-4 w-4 neon-text" />
                <h2 className="font-display text-sm font-bold uppercase tracking-wider">Full League Schedule</h2>
                <span className="ml-auto text-xs text-zinc-500">Preseason beta + Regular Season Weeks 1–18</span>
              </div>
              {data.weeks.map((wk) => <LeagueWeekBlock key={wk.index} wk={wk} />)}
            </div>
          )}

          {/* Standings — Regular Season primary once active; Preseason becomes history */}
          {(() => {
            const active = data.regular_active;
            const standings = active ? (data.regular_standings || []) : data.beta_standings;
            return (
              <div className="rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] overflow-hidden" data-testid="showdown-standings">
                <div className="flex items-center gap-2 px-5 py-4 border-b border-[#141416]">
                  <Trophy className="h-4 w-4 neon-text" />
                  <h2 className="font-display text-sm font-bold uppercase tracking-wider">{active ? "Regular Season Standings" : "Preseason Beta Standings"}</h2>
                  <span className="ml-auto text-xs text-zinc-500">Ranked by record, then points</span>
                </div>
                <div className="grid grid-cols-[40px_1fr_44px_44px_80px] sm:grid-cols-[60px_1fr_60px_60px_120px] px-5 py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500 border-b border-[#141416]">
                  <span>Rank</span><span>Player</span><span className="text-center">W</span><span className="text-center">L</span><span className="text-right">Points</span>
                </div>
                {standings.map((r) => (
                  <div key={r.user_id} data-testid={`showdown-row-${r.user_id}`}
                    className={`grid grid-cols-[40px_1fr_44px_44px_80px] sm:grid-cols-[60px_1fr_60px_60px_120px] items-center px-5 py-4 border-b border-[#141416] last:border-0 ${r.user_id === user?.id ? "bg-[#D50A0A]/5" : ""}`}>
                    <span className={`font-display font-black ${r.rank <= 3 ? "text-[#D50A0A]" : "text-zinc-500"}`}>{r.rank}</span>
                    <span className="font-semibold text-sm truncate">{r.display_name}{r.user_id === user?.id && <span className="text-zinc-500 font-normal"> (you)</span>}</span>
                    <span className="text-center font-bold text-emerald-400">{r.wins}</span>
                    <span className="text-center font-bold text-zinc-400">{r.losses}{r.ties ? `-${r.ties}` : ""}</span>
                    <span className="text-right font-display font-bold">{r.points}<span className="text-zinc-600 text-xs"> pts</span></span>
                  </div>
                ))}
              </div>
            );
          })()}

          {data.regular_active ? (
            <>
              <p className="text-xs text-zinc-600 mt-3">
                Regular Season is active. Preseason Beta results are archived separately and do not carry over.
              </p>
              <div className="mt-4 rounded-2xl border border-amber-500/25 bg-[#0A0A0A] overflow-hidden" data-testid="preseason-history">
                <button data-testid="toggle-preseason-history" onClick={() => setShowBetaHistory((v) => !v)}
                  className="w-full flex items-center gap-2 px-5 py-4 text-left">
                  <Swords className="h-4 w-4 text-amber-400" />
                  <h2 className="font-display text-sm font-bold uppercase tracking-wider text-amber-300">Preseason Beta History</h2>
                  <span className="ml-auto inline-flex items-center gap-1 text-xs text-zinc-400">
                    {showBetaHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    {showBetaHistory ? "Hide" : "View Preseason Results"}
                  </span>
                </button>
                {showBetaHistory && (
                  <div>
                    <div className="grid grid-cols-[40px_1fr_44px_44px_80px] sm:grid-cols-[60px_1fr_60px_60px_120px] px-5 py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500 border-y border-[#141416]">
                      <span>Rank</span><span>Player</span><span className="text-center">W</span><span className="text-center">L</span><span className="text-right">Points</span>
                    </div>
                    {data.beta_standings.map((r) => (
                      <div key={r.user_id} data-testid={`beta-history-row-${r.user_id}`}
                        className="grid grid-cols-[40px_1fr_44px_44px_80px] sm:grid-cols-[60px_1fr_60px_60px_120px] items-center px-5 py-3 border-b border-[#141416] last:border-0">
                        <span className={`font-display font-black ${r.rank <= 3 ? "text-amber-400" : "text-zinc-500"}`}>{r.rank}</span>
                        <span className="font-semibold text-sm truncate">{r.display_name}</span>
                        <span className="text-center font-bold text-emerald-400/80">{r.wins}</span>
                        <span className="text-center font-bold text-zinc-500">{r.losses}{r.ties ? `-${r.ties}` : ""}</span>
                        <span className="text-right font-display font-bold text-zinc-300">{r.points}<span className="text-zinc-600 text-xs"> pts</span></span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="text-xs text-zinc-600 mt-3">
              Regular-season matchups are scheduled but NOT yet active — no wins, losses, or points count until the commissioner activates the regular season. Preseason beta stays separate and won't carry over.
            </p>
          )}
        </>
      )}
    </PageShell>
  );
}
